import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import * as Icons from "lucide-react";
import { Phone, MessageCircle, ArrowRight, Star, ShieldCheck, Mail } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import { Navbar, Footer, Reveal, Section, ProductCard, ease } from "@/components/shared";
import { site, categorie, whatsappHref } from "@/config/site";
import { api } from "@/lib/api";

const headline = ["Onoranze Funebri", "PAX"];

const Intro = () => (
  <section className="relative overflow-hidden bg-[hsl(214_30%_96%)]">
    <div
      className="pointer-events-none absolute inset-0"
      style={{
        background:
          "radial-gradient(ellipse 80% 60% at 50% 0%, hsl(35 34% 64% / 0.18), transparent 55%), linear-gradient(180deg, hsl(214 40% 94%) 0%, hsl(220 20% 97%) 55%, hsl(210 40% 99%) 100%)",
      }}
    />
    <div className="relative max-w-4xl mx-auto px-6 md:px-12 pt-4 md:pt-6 pb-12 md:pb-16 text-center flex flex-col items-center">
      <h1 className="font-serif font-light text-primary text-4xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
        {headline.map((line, i) => (
          <span key={i} className="line-mask">
            <motion.span className="block"
              initial={{ y: "110%" }} animate={{ y: 0 }}
              transition={{ duration: 1.1, delay: 0.25 + i * 0.13, ease }}>
              {line}
            </motion.span>
          </span>
        ))}
      </h1>
      <motion.p className="mt-4 overline"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7, duration: 0.9 }}>
        dal 1984
      </motion.p>

      <motion.p className="mt-10 text-muted-foreground text-lg md:text-xl leading-relaxed max-w-xl"
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.9, ease }}>
        Accanto alle famiglie nei momenti più delicati, con discrezione e competenza.
      </motion.p>

      <motion.a href={`tel:${site.telefonoRaw}`} data-testid="hero-contatti"
        className="mt-6 inline-flex items-center gap-2.5 text-primary hover:text-accent transition-colors"
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 0.8, ease }}>
        <Phone size={20} className="text-accent" />
        <span className="text-2xl md:text-3xl font-serif font-light tracking-wide">{site.telefono}</span>
      </motion.a>

      <motion.div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/60 px-4 py-1.5 text-sm text-primary"
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.8 }}>
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-60" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-accent" />
        </span>
        Disponibilità H24
      </motion.div>

      <motion.div className="mt-8"
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.9, ease }}>
        <Link to="/preventivo" data-testid="hero-preventivo"
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-semibold px-8 py-4 rounded-full hover:-translate-y-0.5 transition-all">
          Crea il tuo preventivo <ArrowRight size={18} />
        </Link>
      </motion.div>
    </div>
  </section>
);

const ChiSiamo = () => (
  <Section id="chi-siamo">
    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
      <div>
        <Reveal>
          <p className="overline mb-4">La nostra storia</p>
          <h2 className="text-4xl md:text-5xl font-serif font-light text-primary">Chi siamo</h2>
        </Reveal>
        <div className="mt-10 space-y-8">
          {site.chiSiamo.map((b) => (
            <Reveal key={b.num}>
              <div className="flex gap-4">
                <span className="font-serif text-accent text-xl w-10 shrink-0">{b.num}</span>
                <div>
                  <h3 className="text-xl font-serif text-primary">{b.titolo}</h3>
                  <p className="mt-2 text-muted-foreground leading-relaxed">{b.testo}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-8">
          <div className="flex flex-wrap gap-2">
            {site.valori.map((v) => (
              <span key={v} className="px-3 py-1.5 rounded-full bg-secondary text-sm text-primary">{v}</span>
            ))}
          </div>
          <p className="mt-6 text-muted-foreground leading-relaxed">{site.doveSiamo}</p>
        </Reveal>
      </div>
      <Reveal delay={0.1}>
        <div className="rounded-2xl overflow-hidden border border-border aspect-[4/5] lg:aspect-[3/4]">
          <img src={site.fotoAzienda} alt={`Sede ${site.nome} a ${site.citta}`} className="w-full h-full object-cover" />
        </div>
      </Reveal>
    </div>
  </Section>
);

const ValueMarquee = () => (
  <div className="bg-primary py-5">
    <Marquee speed={35} gradient={false} autoFill>
      {site.stats.map((s, i) => (
        <span key={i} className="mx-8 inline-flex items-baseline gap-2">
          <span className="text-xl md:text-2xl font-serif text-accent">{s.valore}</span>
          <span className="text-sm md:text-base text-white/60">{s.label}</span>
          <span className="text-white/20 ml-6">/</span>
        </span>
      ))}
    </Marquee>
  </div>
);

const Servizi = () => (
  <Section id="servizi" alt className="!py-16 md:!py-24">
    <Reveal><p className="overline mb-4">I nostri servizi</p>
      <h2 className="text-4xl md:text-5xl font-serif font-light text-primary">Un servizio completo, in ogni dettaglio</h2>
    </Reveal>
    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
      {site.servizi.map((s, i) => {
        const Icon = Icons[s.icon] || Icons.Circle;
        return (
          <Reveal key={i} delay={i * 0.05}>
            <Link to={`/servizi/${s.slug}`} data-testid={`servizio-${s.slug}`}
              className="group block h-full bg-card border border-border rounded-xl overflow-hidden hover:-translate-y-1 hover:shadow-md transition-all duration-300">
              <div className="relative aspect-[16/10] overflow-hidden">
                <img src={s.foto} alt={s.titolo} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <span className="absolute bottom-3 left-3 inline-flex items-center justify-center w-10 h-10 rounded-full bg-accent text-primary"><Icon size={18} /></span>
              </div>
              <div className="p-5">
                <h3 className="text-xl font-serif text-primary">{s.titolo}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.testo}</p>
                <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary group-hover:gap-2.5 transition-all">
                  Scopri di più <ArrowRight size={15} />
                </span>
              </div>
            </Link>
          </Reveal>
        );
      })}
    </div>
  </Section>
);

const Catalogo = () => {
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    api.categories().then((c) => { setCats(c); setActive(c[0]?.categoria); }).catch(() => setError(true));
  }, []);
  useEffect(() => {
    if (!active) return;
    setLoading(true);
    setError(false);
    api.catalog(active).then(setItems).catch(() => { setItems([]); setError(true); }).finally(() => setLoading(false));
  }, [active]);
  return (
    <Section id="catalogo">
      <Reveal><p className="overline mb-4">Catalogo &amp; servizi</p>
        <h2 className="text-4xl md:text-5xl font-serif font-light text-primary">Prodotti selezionati con cura</h2>
        <p className="mt-3 text-muted-foreground max-w-xl">Scelga un articolo per aggiungerlo al preventivo, oppure apra la pagina della categoria.</p>
      </Reveal>
      <div className="mt-10 flex flex-wrap gap-3">
        {cats.map((c) => (
          <button key={c.categoria} data-testid={`cat-${c.categoria}`} onClick={() => setActive(c.categoria)}
            className={`px-5 py-2.5 rounded-full text-sm border transition-all ${
              active === c.categoria ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-accent"
            }`}>
            {categorie[c.categoria]?.label || c.categoria}
          </button>
        ))}
      </div>
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
        {loading && [...Array(4)].map((_, i) => (
          <div key={i} className="rounded-xl border border-border bg-card overflow-hidden animate-pulse">
            <div className="aspect-[4/3] bg-secondary" />
            <div className="p-5 space-y-2">
              <div className="h-4 bg-secondary rounded w-2/3" />
              <div className="h-3 bg-secondary rounded w-full" />
            </div>
          </div>
        ))}
        {!loading && error && (
          <p className="col-span-full text-muted-foreground">
            Catalogo temporaneamente non disponibile. Ci chiami al{" "}
            <a href={`tel:${site.telefonoRaw}`} className="text-primary font-semibold">{site.telefono}</a>.
          </p>
        )}
        {!loading && !error && items.length === 0 && (
          <p className="col-span-full text-muted-foreground">Nessun prodotto in questa categoria.</p>
        )}
        {!loading && items.slice(0, 8).map((p, i) => (
          <Reveal key={p.id} delay={(i % 4) * 0.05}>
            <ProductCard p={p} onClick={() => navigate(`/preventivo?prodotto=${p.id}`)} testid={`catalog-item-${p.id}`} />
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-12 flex flex-wrap gap-6 items-center">
        <Link to={`/catalogo/${active || "bare"}`} data-testid="catalog-vedi-tutti"
          className="inline-flex items-center gap-2 text-primary font-semibold border-b-2 border-accent pb-1 hover:gap-3 transition-all">
          Vedi tutta la categoria {categorie[active]?.label} <ArrowRight size={18} />
        </Link>
        <Link to="/preventivo" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
          Oppure configura il tuo preventivo <ArrowRight size={16} />
        </Link>
      </Reveal>
    </Section>
  );
};

const Recensioni = () => (
  <Section alt>
    <Reveal><p className="overline mb-4">Testimonianze</p>
      <h2 className="text-4xl md:text-5xl font-serif font-light text-primary">La fiducia delle famiglie</h2>
    </Reveal>
    <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
      {site.recensioni.map((r, i) => (
        <Reveal key={i} delay={i * 0.06}>
          <div className="h-full bg-card border border-border rounded-xl p-7">
            <div className="flex gap-1 text-accent">{[...Array(5)].map((_, k) => <Star key={k} size={15} fill="currentColor" />)}</div>
            <p className="mt-4 text-primary/90 leading-relaxed font-serif text-lg italic">“{r.testo}”</p>
            <p className="mt-5 text-sm font-semibold text-primary">{r.nome}</p>
            <p className="text-xs text-muted-foreground">{r.luogo}</p>
          </div>
        </Reveal>
      ))}
    </div>
    <div className="mt-16 grid sm:grid-cols-2 gap-10 border-t border-border pt-12">
      <div>
        <p className="overline mb-4 flex items-center gap-2"><ShieldCheck size={16} /> Certificazioni</p>
        <div className="flex flex-wrap gap-3">
          {site.certificazioni.map((c) => <span key={c} className="px-4 py-2 bg-card border border-border rounded-lg text-sm text-primary">{c}</span>)}
        </div>
      </div>
      <div>
        <p className="overline mb-4">Partner</p>
        <div className="flex flex-wrap gap-3">
          {site.partner.map((c) => <span key={c} className="px-4 py-2 bg-card border border-border rounded-lg text-sm text-muted-foreground">{c}</span>)}
        </div>
      </div>
    </div>
  </Section>
);

const Contatti = () => {
  const [form, setForm] = useState({ nome: "", email: "", telefono: "", messaggio: "" });
  const [privacy, setPrivacy] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!privacy) return toast.error("Accetti l'informativa privacy per inviare il messaggio.");
    setLoading(true);
    try {
      await api.contact(form);
      toast.success("Messaggio inviato. La ricontatteremo al più presto.");
      setForm({ nome: "", email: "", telefono: "", messaggio: "" });
      setPrivacy(false);
    } catch { toast.error("Errore nell'invio. Riprovi."); }
    finally { setLoading(false); }
  };
  const fieldClass = "mt-1.5 w-full border border-border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-accent";
  return (
    <Section id="contatti">
      <div className="grid lg:grid-cols-2 gap-14">
        <div>
          <p className="overline mb-4">Contatti</p>
          <h2 className="text-4xl md:text-5xl font-serif font-light text-primary leading-tight">Siamo qui, in ogni momento</h2>
          <p className="mt-5 text-muted-foreground leading-relaxed">{site.orari}</p>
          <div className="mt-8 space-y-4">
            <a href={`tel:${site.telefonoRaw}`} data-testid="contact-phone" className="flex items-center gap-4 group">
              <span className="w-12 h-12 rounded-full bg-accent/15 text-accent flex items-center justify-center"><Phone size={20} /></span>
              <span className="text-lg text-primary group-hover:text-accent transition-colors">{site.telefono}</span>
            </a>
            <a href={whatsappHref()} target="_blank" rel="noreferrer" data-testid="contact-whatsapp" className="flex items-center gap-4 group">
              <span className="w-12 h-12 rounded-full bg-accent/15 text-accent flex items-center justify-center"><MessageCircle size={20} /></span>
              <span className="text-lg text-primary group-hover:text-accent transition-colors">WhatsApp</span>
            </a>
            <a href={`mailto:${site.email}`} className="flex items-center gap-4 group">
              <span className="w-12 h-12 rounded-full bg-accent/15 text-accent flex items-center justify-center"><Mail size={20} /></span>
              <span className="text-lg text-primary group-hover:text-accent transition-colors">{site.email}</span>
            </a>
          </div>
          <div className="mt-8 rounded-xl overflow-hidden border border-border h-64">
            <iframe title="mappa" src={site.mapEmbed} className="w-full h-full" loading="lazy" />
          </div>
        </div>
        <form onSubmit={submit} data-testid="contact-form" className="bg-card border border-border rounded-2xl p-8 md:p-10 self-start">
          <h3 className="text-2xl font-serif text-primary">Scrivici un messaggio</h3>
          <label className="block mt-4 text-sm font-medium text-primary">
            Nome e cognome
            <input required type="text" data-testid="contact-nome" placeholder="Nome e cognome"
              value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })}
              className={fieldClass} />
          </label>
          <label className="block mt-4 text-sm font-medium text-primary">
            Email
            <input required type="email" data-testid="contact-email" placeholder="Email"
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className={fieldClass} />
          </label>
          <label className="block mt-4 text-sm font-medium text-primary">
            Telefono <span className="font-normal text-muted-foreground">(facoltativo)</span>
            <input type="tel" data-testid="contact-telefono" placeholder="Telefono"
              value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })}
              className={fieldClass} />
          </label>
          <label className="block mt-4 text-sm font-medium text-primary">
            Messaggio
            <textarea required rows={4} placeholder="Come possiamo aiutarla?" data-testid="contact-messaggio"
              value={form.messaggio} onChange={(e) => setForm({ ...form, messaggio: e.target.value })}
              className={fieldClass} />
          </label>
          <label className="mt-4 flex items-start gap-3 text-sm text-muted-foreground">
            <input type="checkbox" required checked={privacy} onChange={(e) => setPrivacy(e.target.checked)}
              data-testid="contact-privacy" className="mt-1" />
            <span>Ho letto e accetto l’<Link to="/privacy" className="text-primary underline underline-offset-2">informativa privacy</Link>.</span>
          </label>
          <button type="submit" disabled={loading} data-testid="contact-submit"
            className="mt-5 w-full bg-primary text-primary-foreground py-4 rounded-full font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60">
            {loading ? "Invio in corso..." : "Invia messaggio"}
          </button>
        </form>
      </div>
    </Section>
  );
};

const Faq = () => (
  <Section id="faq" alt>
    <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-12">
      <Reveal><p className="overline mb-4">Domande frequenti</p>
        <h2 className="text-4xl md:text-5xl font-serif font-light text-primary leading-tight">Rispondiamo ai vostri dubbi</h2>
      </Reveal>
      <Reveal delay={0.1}>
        <Accordion type="single" collapsible className="w-full">
          {site.faq.map((f, i) => (
            <AccordionItem key={i} value={`i${i}`} data-testid={`faq-${i}`}>
              <AccordionTrigger className="text-left font-serif text-xl text-primary hover:text-accent">{f.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-base leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </Section>
);

const CtaFinale = () => (
  <section className="relative bg-primary text-primary-foreground grain overflow-hidden">
    <div className="max-w-4xl mx-auto px-6 py-24 md:py-32 text-center relative">
      <Reveal>
        <p className="overline mb-6">Un aiuto immediato</p>
        <h2 className="text-4xl md:text-6xl font-serif font-light leading-tight">Siamo al vostro fianco,<br />in qualsiasi momento.</h2>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <Link to="/preventivo" data-testid="cta-preventivo" className="inline-flex items-center gap-2 bg-accent text-primary font-semibold px-8 py-4 rounded-full hover:-translate-y-0.5 transition-all">
            Richiedi preventivo <ArrowRight size={18} />
          </Link>
          <a href={`tel:${site.telefonoRaw}`} className="inline-flex items-center gap-2 border border-white/40 px-8 py-4 rounded-full hover:bg-white/10 transition-all">
            <Phone size={18} /> {site.telefono}
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

const PreventivoBand = () => (
  <section id="preventivo-band" className="relative bg-primary text-primary-foreground grain overflow-hidden">
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-20 md:py-28 relative text-center">
      <Reveal>
        <h2 className="text-4xl md:text-6xl font-serif font-light leading-[1.05]">
          Crea il tuo preventivo<br />in pochi passi
        </h2>
        <p className="mt-6 text-white/70 text-lg leading-relaxed max-w-md mx-auto">
          Scegli ogni dettaglio del funerale e blocca il preventivo.
        </p>
        <Link to="/preventivo" data-testid="band-preventivo"
          className="mt-8 inline-flex items-center gap-2 bg-accent text-primary font-semibold px-10 py-4 rounded-full hover:-translate-y-0.5 transition-all text-lg tracking-wide">
          INIZIA <ArrowRight size={20} />
        </Link>
      </Reveal>
    </div>
  </section>
);

export default function Landing() {
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;
    const t = setTimeout(() => document.querySelector(hash)?.scrollIntoView(), 80);
    return () => clearTimeout(t);
  }, []);
  return (
    <div data-testid="landing-page">
      <Navbar />
      <main className="pt-header">
        <Intro />
        <ValueMarquee />
        <ChiSiamo />
        <PreventivoBand />
        <Servizi />
        <Catalogo />
        <Recensioni />
        <Contatti />
        <Faq />
        <CtaFinale />
      </main>
      <Footer />
    </div>
  );
}
